import React, { useState, useEffect, useRef } from 'react';
import { 
    User, Mail, Lock, LogOut, ChevronRight, 
    X, ArrowLeft, Save, Loader2,
    Eye, EyeOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- CONFIG ---
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";

const Profile = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    
    // --- State ---
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Modals
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    // Edit Profile Form
    const [editData, setEditData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        profile_picture: null,
        preview_url: null     
    });

    // Password Form
    const [passForm, setPassForm] = useState({ 
        current_password: '', 
        new_password: '', 
        confirm_new_password: '' 
    });
    
    // Password Visibility Toggles
    const [showPass, setShowPass] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [submitting, setSubmitting] = useState(false);

    // --- 1. FETCH PROFILE ---
    const fetchProfile = async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            navigate('/'); 
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/app/profile`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const res = await response.json();

            if (res.success) {
                setUser(res.data);
                setEditData({
                    first_name: res.data.first_name || '',
                    last_name: res.data.last_name || '',
                    email: res.data.email || '',
                    profile_picture: null,
                    // Direct usage: API url OR Default Avatar
                    preview_url: res.data.profile_picture || DEFAULT_AVATAR
                });
            }
        } catch (error) {
            console.error("Profile Fetch Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    // Debug: when `user` loads, log profile_picture and try a HEAD request
    useEffect(() => {
        if (user?.profile_picture) {
            console.log('profile_picture url:', user.profile_picture);
            fetch(user.profile_picture, { method: 'HEAD' })
                .then(res => console.log('HEAD', res.status, res.headers.get('content-type')))
                .catch(err => console.error('fetch HEAD error (CORS or network):', err));
        }
    }, [user]);

    // --- 2. IMAGE HANDLING ---
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditData({
                ...editData,
                profile_picture: file,
                preview_url: URL.createObjectURL(file)
            });
        }
    };

    // --- 3. UPDATE PROFILE ---
    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const token = localStorage.getItem('authToken');

        try {
            const formData = new FormData();
            formData.append('first_name', editData.first_name);
            formData.append('last_name', editData.last_name);
            
            if (editData.profile_picture) {
                formData.append('profile_picture', editData.profile_picture);
            }

            const response = await fetch(`${API_BASE_URL}/app/update-profile`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData 
            });
            
            const res = await response.json();

            if (res.success) {
                setUser(res.data);
                setShowEditModal(false);
                setEditData(prev => ({ ...prev, profile_picture: null })); 
                alert("Profile Updated Successfully!");
            } else {
                alert(res.message || "Failed to update profile");
            }
        } catch (error) {
            console.error("Update Error:", error);
            alert("Something went wrong updating profile.");
        } finally {
            setSubmitting(false);
        }
    };

    // --- 4. CHANGE PASSWORD ---
    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (passForm.new_password !== passForm.confirm_new_password) {
            alert("New passwords do not match!");
            return;
        }

        setSubmitting(true);
        const token = localStorage.getItem('authToken');

        try {
            const response = await fetch(`${API_BASE_URL}/app/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(passForm)
            });
            const res = await response.json();

            if (res.success) {
                 alert("Password Changed Successfully!");
                 setShowPasswordModal(false);
                 setPassForm({ current_password: '', new_password: '', confirm_new_password: '' });
            } else {
                alert(res.message || "Failed to change password");
            }
        } catch (error) {
            console.error("Password Error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    // --- 5. LOGOUT ---
    const handleLogout = () => {
        if(window.confirm("Are you sure you want to logout?")) {
            localStorage.clear();
            navigate('/');
            window.location.reload();
        }
    };

    // --- RENDER ---
    if (loading) return <div className="flex h-screen items-center justify-center text-[#1A87CF]"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans pb-28">
            
            {/* --- HEADER IMAGE --- */}
            <div className="relative h-48 bg-gradient-to-br from-[#1A87CF] to-[#0d5c91] rounded-b-[3rem] shadow-lg overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#4FB0F0]/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl"></div>
                
                <div className="absolute top-6 left-4 z-10">
                    <button onClick={() => navigate(-1)} className="p-2.5 bg-white/20 backdrop-blur-md border border-white/30 rounded-full text-white hover:bg-white/30 transition-all">
                        <ArrowLeft size={20} />
                    </button>
                </div>
                <div className="absolute top-7 left-0 right-0 text-center pointer-events-none">
                    <h1 className="text-lg font-bold text-white tracking-wide">My Profile</h1>
                </div>
            </div>

            {/* --- PROFILE CARD --- */}
            <div className="container mx-auto px-4 -mt-20 relative z-10 max-w-lg">
                <div className="bg-white rounded-[2rem] p-6 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-slate-50">
                    
                    <div className="relative inline-block mb-4">
                        <div className="w-32 h-32 rounded-full p-1.5 bg-white shadow-xl mx-auto">
                            <img 
                                // Direct usage: API url OR Default Avatar
                                src={user?.profile_picture || DEFAULT_AVATAR} 
                                alt="Profile" 
                                className="w-full h-full rounded-full object-cover bg-gray-100"
                                onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                            />
                        </div>
                    </div>

                    <h2 className="text-2xl font-bold text-slate-800 mb-1 font-rubik">
                        {user?.first_name} {user?.last_name}
                    </h2>
                    
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-full border border-slate-100 mb-6">
                        <Mail size={12} className="text-slate-400" />
                        <p className="text-slate-500 text-xs font-medium">{user?.email}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                        <button 
                            onClick={() => setShowEditModal(true)}
                            className="w-full py-3.5 bg-[#1A87CF] text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98]"
                        >
                            Edit Personal Details
                        </button>
                    </div>
                </div>
            </div>

            {/* --- SETTINGS MENU --- */}
            <div className="container mx-auto px-4 mt-6 space-y-4 max-w-lg">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider ml-2">Settings</h3>
                
                <button 
                    onClick={() => setShowPasswordModal(true)}
                    className="w-full bg-white rounded-2xl p-4 shadow-sm border border-slate-50 flex items-center justify-between group active:scale-[0.98] transition-all"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-orange-50 text-[#FB9724] flex items-center justify-center border border-orange-100">
                            <Lock size={22} />
                        </div>
                        <div className="text-left">
                            <span className="block text-sm font-bold text-slate-800">Change Password</span>
                            <span className="block text-[11px] text-slate-400 font-medium">Update your security</span>
                        </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:text-[#FB9724] transition-colors">
                        <ChevronRight size={18} />
                    </div>
                </button>

                <button 
                    onClick={handleLogout}
                    className="w-full bg-white rounded-2xl p-4 shadow-sm border border-slate-50 flex items-center justify-between group active:scale-[0.98] transition-all"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center border border-red-100">
                            <LogOut size={22} />
                        </div>
                        <div className="text-left">
                            <span className="block text-sm font-bold text-slate-800">Log Out</span>
                            <span className="block text-[11px] text-slate-400 font-medium">Sign out of your account</span>
                        </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:text-red-500 transition-colors">
                        <ChevronRight size={18} />
                    </div>
                </button>
            </div>

            {/* --- EDIT MODAL --- */}
            {showEditModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
                    <div className="bg-white w-full max-w-sm rounded-[2rem] p-6 shadow-2xl animate-in zoom-in-95 relative overflow-hidden">
                        
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-slate-800">Edit Profile</h3>
                            <button onClick={() => setShowEditModal(false)} className="p-2 bg-slate-50 rounded-full text-slate-500 hover:bg-slate-100 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateProfile} className="space-y-5">
                            <div className="flex justify-center">
                                <div className="relative group cursor-pointer" onClick={() => fileInputRef.current.click()}>
                                    <div className="w-28 h-28 rounded-full border-4 border-slate-50 shadow-md p-1 bg-white overflow-hidden">
                                        <img 
                                            src={editData.preview_url} 
                                            alt="Preview" 
                                            className="w-full h-full rounded-full object-cover group-hover:opacity-80 transition-opacity" 
                                            onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                                        />
                                    </div>
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <span className="bg-black/50 text-white text-[10px] px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
                                            Change
                                        </span>
                                    </div>
                                    <input type="file" ref={fileInputRef} onChange={handleImageChange} className="hidden" accept="image/*" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1.5 ml-1">First Name</label>
                                    <input type="text" value={editData.first_name} onChange={(e) => setEditData({...editData, first_name: e.target.value})} className="w-full p-3.5 bg-slate-50 rounded-xl border-none text-slate-800 text-sm font-semibold focus:ring-2 focus:ring-[#1A87CF]/20 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1.5 ml-1">Last Name</label>
                                    <input type="text" value={editData.last_name} onChange={(e) => setEditData({...editData, last_name: e.target.value})} className="w-full p-3.5 bg-slate-50 rounded-xl border-none text-slate-800 text-sm font-semibold focus:ring-2 focus:ring-[#1A87CF]/20 outline-none" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1.5 ml-1">Email (Read Only)</label>
                                <div className="relative">
                                    <input type="email" value={editData.email} disabled className="w-full p-3.5 bg-slate-100 rounded-xl border-none text-slate-500 text-sm font-medium focus:outline-none cursor-not-allowed pl-10" />
                                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                            </div>

                            <button type="submit" disabled={submitting} className="w-full py-4 bg-[#1A87CF] text-white rounded-xl font-bold text-sm hover:bg-[#1569A3] transition-all shadow-lg shadow-blue-200/50 flex justify-center items-center gap-2 active:scale-[0.98]">
                                {submitting ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />} Save Changes
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* --- PASSWORD MODAL --- */}
            {showPasswordModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
                    <div className="bg-white w-full max-w-sm rounded-[2rem] p-6 shadow-2xl animate-in zoom-in-95">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-slate-800">Update Password</h3>
                            <button onClick={() => setShowPasswordModal(false)} className="p-2 bg-slate-50 rounded-full text-slate-500 hover:bg-slate-100 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleChangePassword} className="space-y-4">
                            {['current', 'new', 'confirm'].map((field, idx) => {
                                const labels = ['Current Password', 'New Password', 'Confirm Password'];
                                const keys = ['current_password', 'new_password', 'confirm_new_password'];
                                const isVisible = showPass[field];

                                return (
                                    <div key={field}>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1.5 ml-1">{labels[idx]}</label>
                                        <div className="relative">
                                            <input 
                                                type={isVisible ? "text" : "password"}
                                                value={passForm[keys[idx]]}
                                                onChange={(e) => setPassForm({...passForm, [keys[idx]]: e.target.value})}
                                                className="w-full p-3.5 bg-slate-50 rounded-xl border-none text-slate-800 text-sm font-semibold focus:ring-2 focus:ring-[#1A87CF]/20 outline-none pr-10"
                                                placeholder="••••••••"
                                            />
                                            <button 
                                                type="button"
                                                onClick={() => setShowPass({ ...showPass, [field]: !isVisible })}
                                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1A87CF]"
                                            >
                                                {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}

                            <button type="submit" disabled={submitting} className="w-full py-4 bg-[#1A87CF] text-white rounded-xl font-bold text-sm hover:bg-[#1569A3] transition-all shadow-lg shadow-blue-200/50 flex justify-center items-center gap-2 mt-2 active:scale-[0.98]">
                                {submitting ? <Loader2 size={20} className="animate-spin" /> : <Lock size={20} />} Change Password
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;